import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import axiosConfig from './axiosConfig';
import {
    buildFirebaseConfigFromEnv,
    buildFirebaseConfigFromServer,
    isFirebaseConfigReady
} from './firebaseConfig';

export const PUSH_PROMPT_STATUS_KEY = 'pushNotificationPromptStatus';

let messagingInstance = null;
let cachedConfig = null;

/**
 * @returns {boolean}
 */
export function isPushNotificationSupported() {
    return (
        typeof window !== 'undefined' &&
        'Notification' in window &&
        'serviceWorker' in navigator
    );
}

/**
 * @returns {Promise<boolean>}
 */
export async function isFirebaseMessagingSupported() {
    try {
        return await isSupported();
    } catch {
        return false;
    }
}

/**
 * @returns {Promise<{ firebaseOptions: object, vapidKey: string } | null>}
 */
export async function resolveFirebaseConfig() {
    if (cachedConfig) {
        return cachedConfig;
    }

    const fromEnv = buildFirebaseConfigFromEnv();
    if (isFirebaseConfigReady(fromEnv)) {
        cachedConfig = fromEnv;
        return cachedConfig;
    }

    try {
        const response = await axiosConfig.get('/students/push-config');
        const merged = buildFirebaseConfigFromServer(response.data);
        if (isFirebaseConfigReady(merged)) {
            cachedConfig = merged;
            return cachedConfig;
        }
    } catch (err) {
        console.warn('Failed to load push config from server', err);
    }

    return null;
}

/**
 * @returns {Promise<ServiceWorkerRegistration | null>}
 */
export async function registerFirebaseServiceWorker() {
    if (!('serviceWorker' in navigator)) {
        return null;
    }

    try {
        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
            scope: '/'
        });
        await navigator.serviceWorker.ready;
        return registration;
    } catch (err) {
        console.error('Service worker registration failed:', err);
        return null;
    }
}

/**
 * Ensure Firebase messaging + service worker are ready.
 * @returns {Promise<{ config: object|null, messaging: import('firebase/messaging').Messaging|null, registration: ServiceWorkerRegistration|null, reason?: string }>}
 */
export async function ensureMessagingReady() {
    const config = await resolveFirebaseConfig();
    if (!config) {
        return { config: null, messaging: null, registration: null, reason: 'not_configured' };
    }

    const supported = await isFirebaseMessagingSupported();
    if (!supported) {
        return { config, messaging: null, registration: null, reason: 'not_supported' };
    }

    const registration = await registerFirebaseServiceWorker();
    if (!registration) {
        return { config, messaging: null, registration: null, reason: 'service_worker_failed' };
    }

    try {
        if (!messagingInstance) {
            const apps = getApps();
            const app = apps.length > 0 ? apps[0] : initializeApp(config.firebaseOptions);
            messagingInstance = getMessaging(app);
        }
        return { config, messaging: messagingInstance, registration };
    } catch (err) {
        console.error('Firebase messaging init failed:', err);
        return { config, messaging: null, registration, reason: 'messaging_init_failed' };
    }
}

/**
 * Show a native browser notification (when permission is granted).
 * @param {string} title
 * @param {string} body
 */
export function showBrowserNotification(title, body) {
    if (!isPushNotificationSupported() || Notification.permission !== 'granted') {
        return;
    }

    try {
        const notification = new Notification(title, {
            body,
            icon: `${process.env.PUBLIC_URL || ''}/favicon.ico`,
            tag: `vector-${Date.now()}`
        });
        notification.onclick = () => {
            window.focus();
            notification.close();
        };
    } catch (err) {
        console.warn('Could not show browser notification:', err);
    }
}

/**
 * @param {string} token
 */
export async function registerFcmTokenWithServer(token) {
    await axiosConfig.post('/students/fcm-token', {
        token,
        platform: 'web'
    });
}

/**
 * Request browser permission and register FCM token with the server.
 * @returns {Promise<{ success: boolean, reason?: string, error?: string }>}
 */
export async function enablePushNotifications() {
    if (!isPushNotificationSupported()) {
        return { success: false, reason: 'unsupported' };
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
        return { success: false, reason: permission === 'denied' ? 'denied' : 'dismissed' };
    }

    const { config, messaging, registration, reason } = await ensureMessagingReady();
    if (!config || !messaging || !registration) {
        return { success: false, reason: reason || 'messaging_unavailable' };
    }

    try {
        const token = await getToken(messaging, {
            vapidKey: config.vapidKey,
            serviceWorkerRegistration: registration
        });

        if (!token) {
            return { success: false, reason: 'no_token' };
        }

        await registerFcmTokenWithServer(token);
        localStorage.setItem(PUSH_PROMPT_STATUS_KEY, 'enabled');
        return { success: true };
    } catch (err) {
        console.error('FCM getToken failed:', err);
        return { success: false, reason: 'token_error', error: err?.message };
    }
}

/**
 * Sync token when permission was granted earlier (no prompt).
 * @returns {Promise<{ synced: boolean, reason?: string }>}
 */
export async function syncPushTokenIfGranted() {
    if (!isPushNotificationSupported() || Notification.permission !== 'granted') {
        return { synced: false, reason: 'permission_not_granted' };
    }

    try {
        const { config, messaging, registration, reason } = await ensureMessagingReady();
        if (!config || !messaging || !registration) {
            return { synced: false, reason };
        }

        const token = await getToken(messaging, {
            vapidKey: config.vapidKey,
            serviceWorkerRegistration: registration
        });

        if (!token) {
            return { synced: false, reason: 'no_token' };
        }

        await registerFcmTokenWithServer(token);
        localStorage.setItem(PUSH_PROMPT_STATUS_KEY, 'enabled');
        return { synced: true };
    } catch (err) {
        console.error('Push token sync failed:', err);
        return { synced: false, reason: 'sync_error', error: err?.message };
    }
}

/**
 * @param {(payload: import('firebase/messaging').MessagePayload) => void} callback
 * @returns {Promise<(() => void) | null>}
 */
export async function subscribeToForegroundMessages(callback) {
    const { messaging, reason } = await ensureMessagingReady();
    if (!messaging) {
        console.warn('Foreground FCM listener not started:', reason);
        return null;
    }

    return onMessage(messaging, callback);
}

/**
 * @param {import('firebase/messaging').MessagePayload} payload
 * @returns {{ title: string, body: string }}
 */
export function getNotificationDisplayFromPayload(payload) {
    const title =
        payload.notification?.title ||
        payload.data?.title ||
        'Vector India';
    const body =
        payload.notification?.body ||
        payload.data?.body ||
        '';
    return { title, body };
}

/**
 * Handle an incoming FCM payload in the active tab.
 * @param {import('firebase/messaging').MessagePayload} payload
 */
export function handleIncomingPushPayload(payload) {
    const { title, body } = getNotificationDisplayFromPayload(payload);
    showBrowserNotification(title, body);
    return { title, body };
}
